/**
 * General Route Guard Middleware
 * Checks permissions BEFORE component mounts
 *
 * Usage in page component:
 *   middleware: 'route-guard',
 *   routeGuard: {
 *     permission: PermissionName.dashboard_omnichannel_view,
 *     redirectUrl: '/404'
 *   }
 *
 * Or with multiple conditions:
 *   middleware: 'route-guard',
 *   routeGuard: {
 *     conditions: [
 *       { permission: PermissionName.inbox_general_view },
 *       { package: 'CP-QONTAKCHAT-2025-0004' }
 *     ],
 *     strategy: 'AND',
 *     redirectUrl: '/404'
 *   }
 */
export default async function routeGuard({ route, store, redirect }) {
  // Get route guard config from page component's options
  const routeGuardConfig =
    route.matched[0]?.components?.default?.options?.routeGuard

  // No config = no guard
  if (!routeGuardConfig) {
    return
  }

  const {
    permission,
    conditions = [],
    strategy = 'AND',
    minimumRequired = 2,
    redirectUrl = '/404',
    defaultAccess = true,
  } = routeGuardConfig

  // Build conditions array
  const allConditions = permission
    ? [{ permission, label: 'USMAN Permission' }, ...conditions]
    : conditions

  // No conditions = allow access
  if (allConditions.length === 0) {
    return
  }

  // Evaluate all conditions
  const results = await Promise.all(
    allConditions.map(async (cond) => {
      // USMAN Permission
      if (cond.permission) {
        const isEnabled = store.state.usman.isUseUsmanPermissions
        if (!isEnabled) return defaultAccess
        return store.getters['usman/isHasPermission'](cond.permission)
      }

      // Package billing
      if (cond.package) {
        const isEnabled = store.state.package.isUsePackageCode
        if (!isEnabled) return defaultAccess
        return store.getters['package/isEnableBillingPackage'](cond.package)
      }

      // Custom condition (async function)
      if (cond.condition) {
        return await cond.condition({ store, route })
      }

      return defaultAccess
    })
  )

  // Apply strategy
  const trueCount = results.filter(Boolean).length
  const totalCount = results.length
  let hasAccess = false

  switch (strategy) {
    case 'AND':
      hasAccess = trueCount === totalCount
      break
    case 'OR':
      hasAccess = trueCount > 0
      break
    case 'MINIMUM':
      hasAccess = trueCount >= minimumRequired
      break
  }

  // Redirect if no access
  if (!hasAccess) {
    return redirect(redirectUrl)
  }
}
